/** Thin client over the Mission Agent read API. One place, so components
 *  never duplicate a fetch. Uses the global axios instance, so Sentio's token
 *  refresh interceptor (UserContext) applies. */

import axios from "axios";

import * as CONSTANTS from "../../utils/constants/constants";
import type {
  ActivityRow,
  CloseoutResponse,
  Dashboard,
  FeedItem,
  MissionListRow,
  MissionSummaryCard,
  SimulationOptions,
  SimulationResponse,
  SignOff,
  SummaryVersion,
  Track,
} from "./types";

const BASE = () => `${CONSTANTS.HOST}api/missionagent`;

async function get<T>(token: string, path: string, params?: URLSearchParams): Promise<T> {
  const response = await axios.get<T>(`${BASE()}${path}`, {
    headers: { authorization: `Bearer ${token}` },
    params,
  });
  return response.data;
}

const mission = (missionId: string) => `/missions/${encodeURIComponent(missionId)}`;

/** This browser's time zone ("Europe/Oslo"). Times are stored in UTC; whatever the server writes times into
 *  for this page (drafts, the care record, the ePCR) writes them in this zone. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** The zone's short name at that moment ("CEST", "GMT+2"): what the page labels times with. */
export function timeZoneLabel(at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? browserTimeZone();
  } catch {
    return browserTimeZone();
  }
}

const zoneParams = () => new URLSearchParams({ tz: browserTimeZone() });

/** The handoff's layouts: IMIST-AMBO (the default), ISBAR or ATMIST(ER). */
export const HANDOVER_FORMATS = ["imist_ambo", "isbar", "atmist"] as const;
export type HandoverFormat = (typeof HANDOVER_FORMATS)[number];

/** The routing map's areas and the layout used there: ATMIST(ER) in the UK, ISBAR in Trøndelag and Oslo,
 *  IMIST-AMBO anywhere else. The UK is four boxes, so the Republic of Ireland's east coast (Dublin) stays
 *  out; a strip of Donegal and Monaghan by the Northern Ireland border still falls in. */
const LAYOUT_AREAS: { area: string; format: HandoverFormat; west: number; south: number; east: number; north: number }[] = [
  { area: "the UK", format: "atmist", west: -5.8, south: 49.8, east: 1.8, north: 55.0 }, // England, Wales, Isle of Man
  { area: "the UK", format: "atmist", west: -5.9, south: 55.0, east: -1.4, north: 55.4 }, // southern Scotland
  { area: "the UK", format: "atmist", west: -8.7, south: 55.4, east: -0.5, north: 61.0 }, // Scotland and its islands
  { area: "the UK", format: "atmist", west: -7.65, south: 54.0, east: -5.4, north: 55.3 }, // Northern Ireland
  { area: "Trøndelag", format: "isbar", west: 8.4, south: 62.2, east: 14.6, north: 64.6 },
  { area: "Oslo", format: "isbar", west: 10.0, south: 59.55, east: 11.4, north: 60.25 },
];

/** The layout for a receiving facility at `at` (none selected yet: IMIST-AMBO), and the area that chose it. */
export function areaLayout(at: { lat: number; lng: number } | null | undefined): {
  format: HandoverFormat;
  area: string | null;
} {
  const found = at
    ? LAYOUT_AREAS.find((a) => at.lat >= a.south && at.lat <= a.north && at.lng >= a.west && at.lng <= a.east)
    : undefined;
  return found ? { format: found.format, area: found.area } : { format: "imist_ambo", area: null };
}

/** The layout the page shows now (the drafts and the PDF are asked for in it). */
let handover: HandoverFormat = "imist_ambo";

export const handoverFormat = (): HandoverFormat => handover;

export function setHandoverFormat(next: HandoverFormat): void {
  handover = next;
}

/** How this reader sees drafts: times in their zone, the handoff in their layout. */
const viewParams = () => {
  const params = zoneParams();
  params.set("handover", handoverFormat());
  return params;
};

export function fetchDeviceMissions(token: string, deviceId: string): Promise<MissionListRow[]> {
  return get(token, `/devices/${encodeURIComponent(deviceId)}/missions/`);
}

export function fetchDashboard(token: string, missionId: string): Promise<Dashboard> {
  return get(token, `${mission(missionId)}/dashboard/`, viewParams());
}

export function fetchActivity(
  token: string,
  missionId: string,
  kind: "event" | "agent_run",
  limit = 60,
): Promise<ActivityRow[]> {
  return get(token, `${mission(missionId)}/activity/`, new URLSearchParams({ kind, limit: String(limit) }));
}

/** The minimap: the ambulance's positions (only those after `since` when given), its facility and the road. */
export function fetchTrack(token: string, missionId: string, since?: string): Promise<Track> {
  return get(token, `${mission(missionId)}/track/`, since ? new URLSearchParams({ since }) : undefined);
}

/** Recent Updates: engine events and drafts as one feed, newest first. */
export function fetchUpdates(token: string, missionId: string, limit = 50): Promise<FeedItem[]> {
  return get(token, `${mission(missionId)}/updates/`, new URLSearchParams({ limit: String(limit) }));
}

/** Where the mission's close-out is (null `closeout` until someone asks). */
export function fetchCloseout(token: string, missionId: string): Promise<CloseoutResponse> {
  return get(token, `${mission(missionId)}/closeout/`);
}

/** Close the mission out (or retry a failed close-out). Answers the close-out
 *  as it stands: an existing one is returned rather than started twice. */
export async function startCloseout(token: string, missionId: string): Promise<CloseoutResponse> {
  const response = await axios.post<CloseoutResponse>(
    `${BASE()}${mission(missionId)}/closeout/`,
    { time_zone: browserTimeZone() },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return response.data;
}

const simulation = (deviceId: string) => `${BASE()}/devices/${encodeURIComponent(deviceId)}/simulation/`;

/** Whether this user may see the device's raw Sentio inputs (the TEMPORARY Debug dialog): the API answers
 *  403 to anyone but Infiniwell staff and admins of the device's organization. */
export async function mayDebug(token: string, deviceId: string): Promise<boolean> {
  try {
    await get(token, `/devices/${encodeURIComponent(deviceId)}/debug-inputs/`, new URLSearchParams({ limit: "1" }));
    return true;
  } catch {
    return false;
  }
}

/** Whether this user may simulate missions on the device here, and its last simulation. */
export function fetchSimulation(token: string, deviceId: string): Promise<SimulationResponse> {
  return get(token, `/devices/${encodeURIComponent(deviceId)}/simulation/`);
}

/** Start a simulated mission on the device. A 409 (one already running) still answers with it. */
export async function startSimulation(
  token: string,
  deviceId: string,
  options: SimulationOptions,
): Promise<SimulationResponse> {
  const response = await axios.post<SimulationResponse>(simulation(deviceId), options, {
    headers: { authorization: `Bearer ${token}` },
    validateStatus: (status) => status === 202 || status === 409,
  });
  return response.data;
}

/** Stop the device's simulation: the device goes quiet and its mission stays open. */
export async function stopSimulation(token: string, deviceId: string): Promise<SimulationResponse> {
  const response = await axios.delete<SimulationResponse>(simulation(deviceId), {
    headers: { authorization: `Bearer ${token}` },
    validateStatus: (status) => status === 202 || status === 409,
  });
  return response.data;
}

/** End the mission (final) and start its ePCR. Answers the close-out as it stands. */
export async function endMission(token: string, missionId: string): Promise<CloseoutResponse> {
  const response = await axios.post<CloseoutResponse>(
    `${BASE()}${mission(missionId)}/end/`,
    { time_zone: browserTimeZone() },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return response.data;
}

/** Save the summary's situation as its next version (only once the mission has ended). `baseVersion` is the
 *  version being edited: the API refuses the edit (409) if another version is shown by now. */
export async function saveSummarySituation(
  token: string,
  missionId: string,
  baseVersion: number,
  situation: string,
  signOff = false,
): Promise<MissionSummaryCard> {
  const response = await axios.post<MissionSummaryCard>(
    `${BASE()}${mission(missionId)}/summary/`,
    { situation, base_version: baseVersion, ...(signOff ? { sign_off: true } : {}) },
    { headers: { authorization: `Bearer ${token}` }, params: zoneParams() },
  );
  return response.data;
}

/** Save an earlier version's situation as the summary's next version. */
export async function restoreSummaryVersion(
  token: string,
  missionId: string,
  baseVersion: number,
  restoreVersion: number,
): Promise<MissionSummaryCard> {
  const response = await axios.post<MissionSummaryCard>(
    `${BASE()}${mission(missionId)}/summary/`,
    { restore_version: restoreVersion, base_version: baseVersion },
    { headers: { authorization: `Bearer ${token}` }, params: zoneParams() },
  );
  return response.data;
}

/** Sign off `version` of the handoff (or the summary), the version shown: once the mission has ended and the
 *  AI has finished drafting it. With the summary, `saveSummarySituation(..., true)` does it, edit or not. */
export async function signOffDraft(
  token: string,
  missionId: string,
  kind: "handoff_draft" | "mission_summary",
  version: number,
): Promise<SignOff> {
  const response = await axios.post<SignOff>(
    `${BASE()}${mission(missionId)}/signoff/`,
    { kind, version },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return response.data;
}

/** Every version of the summary, oldest first. */
export function fetchSummaryVersions(token: string, missionId: string): Promise<SummaryVersion[]> {
  return get<SummaryVersion[]>(token, `${mission(missionId)}/summary/versions/`);
}

/** Download the mission's patient care record (PDF) as a file - once the
 *  mission is closed out, the mission handoff (record + device report). The name is
 *  the API's when the browser may read it (cross-origin it usually may not),
 *  else the same name built here. */
export async function downloadCareRecord(
  token: string,
  missionId: string,
  incident: string,
  closedOut = false,
): Promise<void> {
  const prefix = closedOut ? "ePCR" : "patient-care-record";
  await downloadPdf(token, `${mission(missionId)}/care-record.pdf`, zoneParams(), prefix, incident);
}

/** Download the receiving-facility handoff (PDF), in this reader's zone and layout, as the panel shows it. */
export async function downloadHandoff(token: string, missionId: string, incident: string): Promise<void> {
  await downloadPdf(token, `${mission(missionId)}/handoff.pdf`, viewParams(), "handoff", incident);
}

/** Save a PDF the API makes as a file, under the API's name or `<prefix>-<incident>-<local time>.pdf`. */
async function downloadPdf(
  token: string,
  path: string,
  params: URLSearchParams,
  prefix: string,
  incident: string,
): Promise<void> {
  let response;
  try {
    response = await axios.get<Blob>(`${BASE()}${path}`, {
      headers: { authorization: `Bearer ${token}` },
      params,
      responseType: "blob",
    });
  } catch (error) {
    // A blob request answers an error as a blob: read the API's `detail` out of it.
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const detail = await error.response.data
        .text()
        .then((t) => (JSON.parse(t) as { detail?: string }).detail)
        .catch(() => undefined);
      if (detail) throw new Error(detail);
    }
    throw error;
  }
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  // Local time, as the API names it (in the zone the page sends).
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const fallback = `${prefix}-${incident.replace(/[^A-Za-z0-9_-]+/g, "-")}-${stamp}.pdf`;
  const disposition = String(response.headers["content-disposition"] ?? "");
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;

  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A readable message for a failed request, preferring the API's `detail`. */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
    if (detail) return detail;
    if (error.response) return `${error.response.status}: ${error.response.statusText}`;
  }
  return (error as Error)?.message ?? String(error);
}
